import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Users, Lightbulb } from 'lucide-react';

export const ChallengeSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useTranslation();

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-background relative overflow-hidden">
      {/* Decorative Line Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute h-px bg-foreground"
            style={{
              width: '100%',
              top: `${i * 5 + 10}%`,
              transform: `rotate(${i % 2 === 0 ? 2 : -2}deg)`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content - Business Challenge */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
              <Building2 size={14} className="text-primary" />
              <span className="text-xs font-medium text-primary uppercase tracking-wider">
                {t('challenge.badge')}
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">
              {t('challenge.title')}
            </h2>

            <p className="text-muted-foreground leading-relaxed">
              {t('challenge.description')}
            </p>

            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <div className="flex items-start gap-3 p-4 bg-secondary/50 rounded-lg">
                <div className="icon-circle shrink-0">
                  <Lightbulb size={20} />
                </div>
                <div>
                  <div className="font-medium text-foreground">{t('challenge.expertise.title')}</div>
                  <div className="text-sm text-muted-foreground">{t('challenge.expertise.description')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-secondary/50 rounded-lg">
                <div className="icon-circle shrink-0">
                  <Users size={20} />
                </div>
                <div>
                  <div className="font-medium text-foreground">{t('challenge.flexibility.title')}</div>
                  <div className="text-sm text-muted-foreground">{t('challenge.flexibility.description')}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Network Visualization */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative bg-gradient-to-br from-secondary to-background rounded-2xl p-8 lg:p-12">
              {/* Network Illustration */}
              <svg viewBox="0 0 400 300" className="w-full h-auto">
                {/* Connection Lines */}
                <motion.g
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={isInView ? { pathLength: 1, opacity: 0.3 } : {}}
                  transition={{ duration: 1.5, delay: 0.5 }}
                >
                  <line x1="100" y1="80" x2="200" y2="150" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <line x1="300" y1="80" x2="200" y2="150" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <line x1="100" y1="220" x2="200" y2="150" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <line x1="300" y1="220" x2="200" y2="150" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <line x1="50" y1="150" x2="200" y2="150" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <line x1="350" y1="150" x2="200" y2="150" stroke="hsl(var(--primary))" strokeWidth="2" />
                </motion.g>

                {/* Nodes */}
                <motion.g
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  {/* Central Node */}
                  <circle cx="200" cy="150" r="30" fill="hsl(var(--primary))" />
                  <text x="200" y="155" textAnchor="middle" fill="hsl(var(--primary-foreground))" fontSize="12" fontWeight="bold">sf</text>
                  
                  {/* Outer Nodes */}
                  <circle cx="100" cy="80" r="18" fill="hsl(var(--secondary))" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <circle cx="300" cy="80" r="18" fill="hsl(var(--secondary))" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <circle cx="100" cy="220" r="18" fill="hsl(var(--secondary))" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <circle cx="300" cy="220" r="18" fill="hsl(var(--secondary))" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <circle cx="50" cy="150" r="14" fill="hsl(var(--secondary))" stroke="hsl(var(--primary))" strokeWidth="2" />
                  <circle cx="350" cy="150" r="14" fill="hsl(var(--secondary))" stroke="hsl(var(--primary))" strokeWidth="2" />
                </motion.g>
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
